import { useState, useCallback, useRef, useEffect } from 'react';
import { generateSpeechSync } from '../services/ttsApi';
import { downloadFileAsBlob } from '../services/fileApi';
import { useAuth } from '../context/AuthContext';
import { sanitizeTextForTts } from '../utils/textProcessing';
import useLocalStorage from './useLocalStorage';

const MAX_CONCURRENT_REQUESTS = 3;

/**
 * A central TTS player hook to manage a queue of audio items,
 * with resource-efficient pre-fetching and volume control.
 */
export const useTtsPlayer = () => {
  const { user } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [error, setError] = useState(null);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
  const [currentSource, setCurrentSource] = useState(null);
  const [playbackRequest, setPlaybackRequest] = useState(null);

  // Volume State (Persisted)
  const [volume, setVolume] = useLocalStorage('tts_volume', 1.0);

  const queueRef = useRef([]);
  const currentIndexRef = useRef(0);
  const audioRef = useRef(null);
  const audioCache = useRef({});
  const activeRequests = useRef(new Set());
  const isMounted = useRef(true);
  const isPausedRef = useRef(false);
  const isPlayingRef = useRef(isPlaying);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Apply volume changes to the audio element immediately
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const processQueue = useCallback(() => {
    if (!isMounted.current || !isPlayingRef.current || isPausedRef.current) return;
    const queue = queueRef.current;
    const currentIndex = currentIndexRef.current;
    for (let i = 0; i < MAX_CONCURRENT_REQUESTS; i++) {
      const lookaheadIndex = currentIndex + i;
      if (lookaheadIndex >= queue.length) break;
      if (activeRequests.current.size >= MAX_CONCURRENT_REQUESTS) break;
      const itemToFetch = queue[lookaheadIndex];
      if (itemToFetch && !audioCache.current[itemToFetch.id] && !activeRequests.current.has(itemToFetch.id)) {
        console.log(`TTSPlayer: Requesting audio for index ${lookaheadIndex} (ID: ${itemToFetch.id}). Active requests: ${activeRequests.current.size}`);
        activeRequests.current.add(itemToFetch.id);
        fetchAndCacheAudio(itemToFetch);
      }
    }
  }, []);

  const clearAudioCache = useCallback(() => {
    Object.values(audioCache.current).forEach(cacheItem => {
      if (cacheItem.url) {
        URL.revokeObjectURL(cacheItem.url);
      }
    });
    audioCache.current = {};
  }, []);

  const clearQueueAndReset = useCallback(() => {
    console.log("TTSPlayer: Clearing queue and resetting state.");
    queueRef.current = [];
    currentIndexRef.current = 0;
    activeRequests.current.clear();
    clearAudioCache();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    if (isMounted.current) {
      setIsPlaying(false);
      setIsPreparing(false);
      setCurrentlyPlayingId(null);
      setCurrentSource(null);
      setError(null);
      isPausedRef.current = false;
    }
  }, [clearAudioCache]);

  const generateAudio = useCallback(async (text, itemId) => {
    const sanitizedText = sanitizeTextForTts(text);
    if (!sanitizedText || !sanitizedText.trim()) {
      console.warn(`TTSPlayer: Skipping audio generation for empty text (ID: ${itemId}).`);
      return null;
    }
    try {
      const preferredVoice = user?.preferred_voice || null;
      const ttsResponse = await generateSpeechSync(sanitizedText, preferredVoice);
      if (!ttsResponse || !ttsResponse.resource_id) {
        throw new Error("Failed to get audio resource from TTS API.");
      }
      const audioBlob = await downloadFileAsBlob(ttsResponse.resource_id);
      return URL.createObjectURL(audioBlob);
    } catch (err) {
      console.error(`TTSPlayer: Error generating audio for item ${itemId}:`, err);
      if (isMounted.current) {
        setError(err.message || "Failed to generate audio.");
      }
      return null;
    }
  }, [user]);

  const fetchAndCacheAudio = useCallback(async (item) => {
    if (!item || !item.id) return null;
    if (audioCache.current[item.id]?.url) {
      return audioCache.current[item.id].url;
    }
    if (audioCache.current[item.id]?.promise) {
      return await audioCache.current[item.id].promise;
    }
    const promise = generateAudio(item.text, item.id).then(url => {
      if (url && isMounted.current && audioCache.current[item.id]) {
        audioCache.current[item.id].url = url;
      }
      activeRequests.current.delete(item.id);
      processQueue();
      return url;
    }).catch(err => {
        activeRequests.current.delete(item.id);
        processQueue();
        return null;
    });
    audioCache.current[item.id] = { promise };
    return await promise;
  }, [generateAudio, processQueue]);

  const playItemAtIndex = useCallback(async (index) => {
    if (!isMounted.current || index >= queueRef.current.length) {
      console.log("TTSPlayer: Reached end of queue.");
      clearQueueAndReset();
      return;
    }
    const item = queueRef.current[index];
    currentIndexRef.current = index;
    setCurrentlyPlayingId(item.id);
    setIsPreparing(true);
    const audioUrl = await fetchAndCacheAudio(item);
    if (audioUrl && isMounted.current && isPlayingRef.current && !isPausedRef.current) {
      audioRef.current.src = audioUrl;
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          if (isMounted.current) {
            setIsPreparing(false);
            console.log(`TTSPlayer: Now playing index ${index} (ID: ${item.id})`);
            processQueue();
          }
        }).catch(playError => {
          if (playError.name === 'AbortError') {
            console.log('TTSPlayer: Playback aborted, likely due to rapid state change.');
            if(isMounted.current) setIsPreparing(false);
            return;
          }
          console.error("TTSPlayer: play() promise rejected:", playError);
          if (isMounted.current) {
            setError("Could not play audio. Please ensure your browser allows autoplay.");
            clearQueueAndReset();
          }
        });
      } else {
        setIsPreparing(false);
      }
    } else {
      if (isMounted.current) {
        setIsPreparing(false);
        if (!audioUrl) {
            console.warn(`TTSPlayer: Failed to get audio URL for index ${index}. Skipping.`);
            setTimeout(() => playItemAtIndex(index + 1), 50);
        } else {
            console.log(`TTSPlayer: Audio ready for index ${index}, but playback is paused or stopped. isPlaying=${isPlayingRef.current}, isPaused=${isPausedRef.current}`);
        }
      }
    }
  }, [fetchAndCacheAudio, processQueue, clearQueueAndReset]);

  const playNextItem = useCallback(() => {
    playItemAtIndex(currentIndexRef.current + 1);
  }, [playItemAtIndex]);

  const startPlayback = useCallback((items, source) => {
    if (!items || items.length === 0 || !source) {
      console.warn("TTSPlayer: startPlayback called with empty or invalid items.");
      return;
    }
    console.log(`TTSPlayer: Requesting new playback from source "${source}" with ${items.length} items.`);
    clearQueueAndReset();
    setPlaybackRequest({ items, source });
  }, [clearQueueAndReset]);

  useEffect(() => {
    if (playbackRequest) {
        const { items, source } = playbackRequest;
        queueRef.current = items;
        setCurrentSource(source);
        isPausedRef.current = false;
        setIsPlaying(true);
        playItemAtIndex(0);
        setPlaybackRequest(null);
    }
  }, [playbackRequest, playItemAtIndex]);

  const stopPlayback = useCallback(() => {
    console.log("TTSPlayer: Stop playback requested.");
    clearQueueAndReset();
  }, [clearQueueAndReset]);

  const pause = useCallback(() => {
    if (isPlayingRef.current) {
      audioRef.current?.pause();
      isPausedRef.current = true;
      setIsPlaying(false);
      console.log("TTSPlayer: Paused.");
    }
  }, []);

  const resume = useCallback(() => {
    if (!isPlayingRef.current && queueRef.current.length > 0) {
      isPausedRef.current = false;
      setIsPlaying(true);
      if (audioRef.current?.paused && audioRef.current.src) {
        audioRef.current.play().catch(e => {
          console.error("TTSPlayer: Error resuming playback:", e);
          setError("Could not resume audio.");
          clearQueueAndReset();
        });
        console.log("TTSPlayer: Resumed.");
      } else {
        console.log("TTSPlayer: Resuming by playing current index.");
        playItemAtIndex(currentIndexRef.current);
      }
    }
  }, [clearQueueAndReset, playItemAtIndex]);

  const togglePlayPause = useCallback(() => {
    if (isPlayingRef.current) {
      pause();
    } else {
      resume();
    }
  }, [pause, resume]);

  useEffect(() => {
    isMounted.current = true;
    audioRef.current = new Audio();
    
    // Apply initial volume
    audioRef.current.volume = volume;

    const handleAudioEnded = () => {
      if (!isMounted.current) return;
      console.log(`TTSPlayer: Finished playing index ${currentIndexRef.current}`);
      playNextItem();
    };
    audioRef.current.addEventListener('ended', handleAudioEnded);
    return () => {
      isMounted.current = false;
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleAudioEnded);
        audioRef.current.pause();
        if (audioRef.current.src) URL.revokeObjectURL(audioRef.current.src);
      }
      clearQueueAndReset();
    };
  }, [playNextItem, clearQueueAndReset]); // Dependencies kept from original, volume handled in separate effect

  return {
    isPlaying,
    isPreparing,
    error,
    currentlyPlayingId,
    currentSource,
    startPlayback,
    stopPlayback,
    togglePlayPause,
    volume,
    setVolume,
  };
};